import { useEffect } from 'react';
import { API_BASE_URL, VAPID_PUBLIC_KEY, IS_LOCALHOST, PREVIEW_OFFLINE } from '../config';
import toast from 'react-hot-toast';

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const useWebPush = (userId: string | undefined) => {
  useEffect(() => {
    // Only run if we have a logged-in user and required APIs are present
    if (!userId) return;
    if (typeof window === 'undefined') return;
    const supports = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
    if (!supports) return;

    const ensureSubscribed = async () => {
      // Skip WebPush subscription only when offline preview is enabled on localhost
      if (IS_LOCALHOST && PREVIEW_OFFLINE) {
        return;
      }
      try {
        // Request browser notification permission
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          toast.error('تم رفض إذن الإشعارات عبر المتصفح.');
          return;
        }

        // Ensure service worker is ready
        const registration = await navigator.serviceWorker.ready;

        // Check existing subscription
        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          if (!VAPID_PUBLIC_KEY) {
            console.warn('VAPID public key is missing. Set VITE_VAPID_PUBLIC_KEY in env.');
            toast.error('مفاتيح الإشعارات عبر الويب غير مضبوطة على الخادم.');
            return;
          }
          const appServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: appServerKey,
          });
        }

        // Prepare payload: send the full subscription object to avoid missing fields
        const subJson = (subscription as any).toJSON ? (subscription as any).toJSON() : subscription;
        const payload: any = {
          userId,
          subscription: subJson,
          endpoint: (subscription as any).endpoint || subJson?.endpoint,
          raw: JSON.stringify(subJson),
        };
        console.log('Sending WebPush subscription payload', payload);

        const res = await fetch(`${API_BASE_URL}/webpush/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const errText = await res.text();
          console.error('Failed to save web push subscription:', errText);
          toast.error('تعذر حفظ الاشتراك في إشعارات الويب.');
        } else {
          console.log('WebPush subscription saved');
        }
      } catch (error) {
        console.error('WebPush subscription error:', error);
        toast.error('حدث خطأ أثناء الاشتراك في إشعارات الويب.');
      }
    };

    // Kick off
    ensureSubscribed();
  }, [userId]);
};